/**
 * Isi tim untuk layar Tim — MURNI.
 *
 * ADA KARENA cakupan `MANAGER` adalah `team`, dan penyelesaiannya berbunyi "dirinya + anggota tim
 * yang ia kelola". Tanpa tim, seorang Manajer hanya melihat dirinya sendiri. Ditemukan 23 Agustus
 * 2026 pada basis data produksi: NOL tim, sementara dua orang sudah berperan Manajer — keduanya
 * selama ini praktis sesempit kurir.
 *
 * Dipisahkan dari komponen karena dua keputusan di dalamnya mudah salah dan tidak akan berbunyi:
 * siapa yang MASIH boleh ditambahkan, dan bagaimana orang tanpa nama ditampilkan.
 */

export interface RosterEntry {
  readonly userId: string;
  readonly name: string;
}

export interface TeamView {
  readonly id: string;
  readonly name: string;
  readonly managerUserId: string;
  readonly managerName: string;
  readonly members: readonly RosterEntry[];
}

/**
 * Nama yang tidak diketahui DINYATAKAN, bukan diganti UUID.
 *
 * Sama seperti dashboard absensi: UUID tidak berarti apa pun bagi pembacanya, dan menampilkannya
 * membuat orang menyangka melihat pengenal yang bermakna. Kalimat ini juga yang membuat "nama
 * belum diisi di HBS" dapat dikenali sebagai masalah data, bukan sebagai orang bernama aneh.
 */
export const NAMA_TIDAK_DIKETAHUI = '(nama tidak diketahui)';

export function namaOrang(roster: readonly RosterEntry[], userId: string): string {
  const orang = roster.find((r) => r.userId === userId);
  const nama = orang?.name?.trim() ?? '';
  return nama.length > 0 ? nama : NAMA_TIDAK_DIKETAHUI;
}

/** Menggabungkan tim dari server dengan nama-nama dari roster. */
export function buildTeamViews(
  teams: readonly {
    readonly id: string;
    readonly name: string;
    readonly managerUserId: string;
    readonly memberUserIds: readonly string[];
  }[],
  roster: readonly RosterEntry[],
): readonly TeamView[] {
  return teams.map((t) => ({
    id: t.id,
    name: t.name,
    managerUserId: t.managerUserId,
    managerName: namaOrang(roster, t.managerUserId),
    members: t.memberUserIds.map((id) => ({ userId: id, name: namaOrang(roster, id) })),
  }));
}

/**
 * Siapa yang masih dapat ditambahkan ke sebuah tim.
 *
 * DUA yang dikeluarkan, dan keduanya karena alasan yang berbeda:
 *
 *   1. Yang SUDAH menjadi anggota — server menolaknya lewat `uq_workforce_team_member`, dan
 *      menawarkan nama yang pasti ditolak hanya membuat orang menekan tombol lalu melihat galat.
 *   2. MANAJERNYA SENDIRI — ia sudah termasuk cakupannya tanpa perlu menjadi anggota
 *      (`resolveVisibleUsers` selalu menyertakan aktor). Menambahkannya tidak menambah apa pun
 *      dan membuat daftar anggota berbohong tentang siapa yang dikelola.
 *
 * Urutannya mengikuti roster apa adanya — roster sudah diurutkan sumbernya, dan mengurutkan ulang
 * di sini membuat posisi nama berpindah antar layar tanpa alasan.
 */
export function kandidatAnggota(
  tim: TeamView,
  roster: readonly RosterEntry[],
): readonly RosterEntry[] {
  const sudah = new Set(tim.members.map((m) => m.userId));
  return roster.filter((r) => !sudah.has(r.userId) && r.userId !== tim.managerUserId);
}

/** Batas panjang nama tim — MENIRU `CreateTeamDto`/`RenameTeamDto` di server, bukan dikarang. */
export const NAMA_TIM_MIN = 2;
export const NAMA_TIM_MAKS = 120;

/**
 * Masalah pada nama tim baru, atau `null` bila boleh dikirim.
 *
 * Satu masalah pada satu waktu, bukan daftar — pola yang sama dengan `passwordProblem`.
 * Menampilkan seluruh pelanggaran sekaligus membuat orang memperbaiki semuanya sambil menebak
 * mana yang benar-benar menghalangi.
 *
 * Nama yang SAMA dengan sekarang bukan masalah: server memperlakukannya sebagai tidak-berubah, dan
 * orang yang menekan simpan tanpa mengubah apa pun tidak sedang keliru.
 */
export function namaTimProblem(nama: string): string | null {
  const bersih = nama.trim();
  if (bersih.length === 0) return 'Nama tim belum diisi.';
  if (bersih.length < NAMA_TIM_MIN) return `Nama tim minimal ${NAMA_TIM_MIN} karakter.`;
  if (bersih.length > NAMA_TIM_MAKS) return `Nama tim maksimal ${NAMA_TIM_MAKS} karakter.`;
  return null;
}
